import axios from "axios";
import { format } from "date-fns";

export const getFirstDepartment = async (
  valueSetter,
  setLoading,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    setLoading(true);
    const response = await axios({
      url: `/commitments/autocomplete`,
      params: {
        matchOn: "org",
        value: "",
        size: 1,
      },
    });
    const values = response.data.values || [];
    const firstDepartment = values[0]
      ? {
          displayText: values[0].displayText || "",
          orgName: values[0].value ? values[0].value.orgName : "",
          orgCode: values[0].value ? values[0].value.orgCode : "",
        }
      : null;
    valueSetter(firstDepartment);
  } catch (error) {
    setAlert("error", error.message, true);
  } finally {
    setLoading(false);
  }
};

export const getDepartmentByMatch = async (
  inputValue,
  setDepartmentNameOptions,
  setLoading,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    setLoading(true);
    const params = {
      matchOn: "org",
      value: inputValue,
      size: 25,
    };
    const response = await axios({
      url: `/commitments/autocomplete`,
      params,
    });
    const values = response.data.values || [];
    const marshalledValues = values.map((dept) => {
      return {
        displayText: dept.displayText || "",
        orgName: dept.value ? dept.value.orgName : "",
        orgCode: dept.value ? dept.value.orgCode : "",
      };
    });
    setDepartmentNameOptions(marshalledValues);
  } catch (error) {
    setAlert("error", error.message, true);
  } finally {
    setLoading(false);
  }
};

export const getBudget = async (
  queryParams,
  setBudget,
  setLoading,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    setLoading(true);
    const response = await axios({
      url: `budgets`,
      params: queryParams,
    });
    const data = response.data || [];
    setBudget(data);
  } catch (error) {
    setAlert("error", error.message);
  } finally {
    setLoading(false);
  }
};

export const getBudgetByComponentId = async (
  componentId,
  budgetFiscalYear,
  setAlert,
  clearAlert,
  setFormData,
  cancelSource,
) => {
  try {
    clearAlert();
    const response = await axios({
      url: `budgets/components/${componentId}/budget-fiscal-year/${budgetFiscalYear}`,
      cancelToken: cancelSource?.token,
    });
    const values = response.data;
    setFormData({
      fyProjectionOne: values.fyProjectionOne,
      fyBudget: values.fyBudget,
      fyPlanOne: values.fyPlanOne,
      fyPlanTwo: values.fyPlanTwo,
      comments: values.comments,
    });
  } catch (error) {
    if (!axios.isCancel(error)) {
      setAlert("error", error.message);
    }
  }
};

export const putBudgetByComponentId = async (
  values,
  componentId,
  budgetFiscalYear,
  setEditComponent,
  setSnackBar,
  setRefresh,
  setInfoPanelDisabled,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    await axios.put(
      `/budgets/components/${componentId}/budget-fiscal-year/${budgetFiscalYear}`,
      values,
    );
    setEditComponent(0);
    setRefresh((value) => value + 1);
    setSnackBar(true);
  } catch (error) {
    setAlert("error", error.message);
  } finally {
    setInfoPanelDisabled(false);
  }
};

export const postBudgetFiscalYear = async ({
  newBudgetFiscalYear,
  budgetFiscalYearValues: formValues,
  setRefreshInformationPanel,
  setOpenDialog,
  setInfoDialogOpen,
  setAlert,
  clearAlert,
}) => {
  try {
    clearAlert();
    const values = {
      budgetFiscalYear: newBudgetFiscalYear,
      initialEditEndDate: toFormatedDate(formValues.initialEditPeriodEndDate),
      initialEditStartDate: toFormatedDate(
        formValues.initialEditPeriodStartDate,
      ),
      revisionEditEndDate: toFormatedDate(formValues.revisionEditPeriodEndDate),
      revisionEditStartDate: toFormatedDate(
        formValues.revisionEditPeriodStartDate,
      ),
    };

    await axios({
      method: "post",
      url: `/budgets/budget-fiscal-years`,
      data: values,
    });
    setInfoDialogOpen(false);
    setOpenDialog(false);
    setRefreshInformationPanel(true);
  } catch (error) {
    setAlert("error", error.message);
  }
};

export const putBudgetFiscalYear = async (
  budgetFiscalYear,
  values,
  setRefreshInformationPanel,
  setOpenDialog,
  setInfoDialogOpen,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    values = {
      initialEditEndDate: toFormatedDate(values.initialEditPeriodEndDate),
      initialEditStartDate: toFormatedDate(values.initialEditPeriodStartDate),
      revisionEditEndDate: toFormatedDate(values.revisionEditPeriodEndDate),
      revisionEditStartDate: toFormatedDate(values.revisionEditPeriodStartDate),
    };

    await axios({
      method: "put",
      url: `/budgets/budget-fiscal-years/${budgetFiscalYear}`,
      data: values,
    });
    setInfoDialogOpen(false);
    setOpenDialog(false);
    setRefreshInformationPanel(true);
  } catch (error) {
    setAlert("error", error.message);
  }
};

export const getBudgetFiscalYear = async (
  setBudgetFiscalYearResponse,
  setBudgetFiscalYear,
  setLoading,
  setAlert,
  clearAlert,
) => {
  try {
    clearAlert();
    setLoading(true);
    const response = await axios(`/budgets/budget-fiscal-years`);
    const data = response.data || {};
    const currentBudgetFiscalYear = data.budgetFiscalYear || null;
    setBudgetFiscalYear(currentBudgetFiscalYear);
    setBudgetFiscalYearResponse(data);
  } catch (error) {
    setAlert("error", error.message);
  } finally {
    setLoading(false);
  }
};

/**
 * Converts a date to the format expected by the API
 * @param {*} date
 * @returns {string} a string in the "MM/dd/yyyy" format
 */
const toFormatedDate = (dt) => {
  if (dt) {
    return format(new Date(dt), "MM/dd/yyyy");
  } else {
    return dt;
  }
};
